/**
 * Scan-queue submission logic shared between the kiosk hook and the
 * Service-Worker / setInterval fallback loop.
 *
 * Behaviour for each item (after the per-item backoff gate):
 *   - 2xx                  → remove from queue
 *   - 4xx (≠ 429)          → remove + log to meta.failed (validation can't
 *                            be retried; same body won't validate later)
 *   - 429 / 5xx / network  → retryCount++, schedule next attempt via
 *                            server-supplied `Retry-After` if present,
 *                            else exponential backoff with ±20% jitter.
 *                            Drop after MAX_RETRIES with a failed-log.
 *   - older than DROP_AFTER_DAYS → drop with reason "stale", never POSTed
 *
 * Per-item gate: items whose `nextAttemptAt > now` are skipped silently —
 * the drain loop polls fast (10 s) and the backoff per item decides the
 * actual retry pace.
 */

import { ApiError, apiFetch } from '@/lib/api-client';
import {
  appendFailed,
  listScanQueue,
  removeScanQueueItem,
  setMeta,
  updateScanQueueItem,
} from '@/lib/idb';
import { getKioskToken } from '@/hooks/useKiosk';

export const MAX_RETRIES = 5;
export const DROP_AFTER_DAYS = 28;
export const DROP_AFTER_MS = DROP_AFTER_DAYS * 24 * 60 * 60 * 1000;

/**
 * Backoff schedule (capped at BACKOFF_MAX_MS, ±BACKOFF_JITTER_RATIO jitter):
 *   retryCount=1 → 5 s
 *   retryCount=2 → 20 s
 *   retryCount=3 → 80 s
 *   retryCount=4 → 320 s
 *   retryCount=5 → 600 s (capped from 1280 s)
 * Total wait between first fail and MAX_RETRIES drop: ~17 min worst case,
 * ~7 min if every retry hits the cap minimum of the jitter window.
 */
export const BACKOFF_BASE_MS = 5_000;
export const BACKOFF_FACTOR = 4;
export const BACKOFF_MAX_MS = 10 * 60 * 1000;
export const BACKOFF_JITTER_RATIO = 0.2;

/**
 * Compute the next-attempt delay for an item that just failed.
 * @param retryCountAfterIncrement - the new retryCount (i.e. 1 means
 *   "this was the first failure"). Values ≤ 0 fall through to the base.
 * @param rng - injectable Math.random for deterministic tests.
 */
export function computeBackoffMs(
  retryCountAfterIncrement: number,
  rng: () => number = Math.random,
): number {
  const exponent = Math.max(0, retryCountAfterIncrement - 1);
  const raw = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * Math.pow(BACKOFF_FACTOR, exponent));
  const jitter = (rng() * 2 - 1) * BACKOFF_JITTER_RATIO * raw;
  return Math.max(0, Math.round(raw + jitter));
}

export interface ProcessQueueResult {
  attempted: number;
  succeeded: number;
  removedClient: number;
  retried: number;
  dropped: number;
  /** Items whose nextAttemptAt was still in the future on this drain. */
  skipped: number;
}

export async function processScanQueue(opts?: {
  now?: number;
  rng?: () => number;
}): Promise<ProcessQueueResult> {
  const now = opts?.now ?? Date.now();
  const rng = opts?.rng ?? Math.random;
  const items = await listScanQueue();
  let attempted = 0;
  let succeeded = 0;
  let removedClient = 0;
  let retried = 0;
  let dropped = 0;
  let skipped = 0;

  for (const item of items) {
    if (item.id === undefined) continue;

    // 28-day stale cutoff — drop without sending. Avoids replaying a check-in
    // weeks after the fact (e.g. iPad sat in a closet over the holidays).
    if (now - item.timestamp > DROP_AFTER_MS) {
      await removeScanQueueItem(item.id);
      await appendFailed({
        reason: 'stale',
        at: now,
        barcode: item.barcode,
        retryCount: item.retryCount,
      });
      dropped += 1;
      continue;
    }

    // Backoff gate — wait until the scheduled retry time. The drain loop
    // is cheap to re-run, so silently skipping is fine.
    if (item.nextAttemptAt > now) {
      skipped += 1;
      continue;
    }

    const token = getKioskToken();
    if (!token) {
      // No token = can't submit; leave the item in place and stop. The next
      // kiosk session will re-pick it up. Don't bump retry count.
      break;
    }

    attempted += 1;
    try {
      await apiFetch('/scan', {
        method: 'POST',
        body: { barcode: item.barcode, offlineSync: true },
        bearerToken: token,
      });
      await removeScanQueueItem(item.id);
      succeeded += 1;
      removedClient += 1;
    } catch (err) {
      // 4xx other than 429 → permanent (validation can't be retried).
      if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 429) {
        await removeScanQueueItem(item.id);
        await appendFailed({
          reason: `http_${err.status}_${err.code}`,
          at: now,
          barcode: item.barcode,
          retryCount: item.retryCount,
        });
        dropped += 1;
        continue;
      }
      // 429 / 5xx / network → backoff + retry. 429 is transient (the next
      // window unlocks the same token); dropping it would lose scans
      // exactly when the queue is largest, which is the worst time.
      const nextCount = item.retryCount + 1;
      if (nextCount > MAX_RETRIES) {
        await removeScanQueueItem(item.id);
        await appendFailed({
          reason: 'max_retries_exceeded',
          at: now,
          barcode: item.barcode,
          retryCount: item.retryCount,
        });
        dropped += 1;
      } else {
        // Server-supplied Retry-After (e.g. on 429 or 503) trumps the local
        // backoff schedule. ApiError.retryAfterMs is set by apiFetch when
        // the response carries the header (RFC 7231 §7.1.3).
        const serverDelay = err instanceof ApiError ? err.retryAfterMs : undefined;
        const delay = serverDelay ?? computeBackoffMs(nextCount, rng);
        await updateScanQueueItem({
          ...item,
          retryCount: nextCount,
          nextAttemptAt: now + delay,
        });
        retried += 1;
      }
    }
  }

  if (succeeded > 0 || removedClient > 0 || dropped > 0) {
    await setMeta('lastSyncAt', now);
  }

  return { attempted, succeeded, removedClient, retried, dropped, skipped };
}
